import { useEffect, useState } from 'react';
import { brand, brandDomain, useBrandLogo } from '@/brands';

/**
 * Full-screen brand-aware "site is down" screen.
 *
 * Two operator switches land here and they mean different things to a player,
 * so the copy (not the layout) changes:
 *   • Maintenance Mode ON  — the site exists and is being worked on.
 *   • Site Live Mode OFF   — the brand has not opened yet.
 *
 * Everything is painted from the CSS variables the brand theme already sets
 * (`--brand-primary`, `--brand-header-from/to`), so a white-label gets its own
 * colours here without this file knowing anything about it.
 */

type Mode = 'maintenance' | 'comingSoon';

const COPY: Record<Mode, { badge: string; title: string; lead: string; sub: string }> = {
  maintenance: {
    badge: 'Under Maintenance',
    title: "We'll be back shortly",
    lead: 'Site abhi maintenance par hai.',
    sub: 'Hum kuch zaroori updates kar rahe hain taaki sab kuch aur tez aur smooth chale. Aapka balance aur saari bets bilkul safe hain.',
  },
  comingSoon: {
    badge: 'Coming Soon',
    title: 'Something good is on the way',
    lead: 'Site abhi live nahi hui hai.',
    sub: 'Hum launch ki taiyari kar rahe hain. Thodi der baad wapas aayein — bahut jald darwaze khul rahe hain.',
  },
};

/** Poll the brand endpoint; the moment the operator flips it back, reload. */
function useAutoRecover(): number {
  const [seconds, setSeconds] = useState(30);

  useEffect(() => {
    const apiBase = (
      import.meta.env.DEV
        ? '/api'
        : (import.meta.env.VITE_API_BASE_URL as string) || '/api'
    ).replace(/\/$/, '');

    const tick = setInterval(() => setSeconds((s) => (s > 0 ? s - 1 : 0)), 1000);

    const check = setInterval(async () => {
      setSeconds(30);
      try {
        const res = await fetch(`${apiBase}/brand`, {
          credentials: 'include',
          cache: 'no-store',
          headers: { 'X-Brand-Domain': brandDomain() },
        });
        if (!res.ok) return;
        const b = await res.json();
        const s = b?.settings;
        if (s && s.maintenance === false && s.siteLive === true) {
          window.location.reload();
        }
      } catch {
        /* still down — keep waiting */
      }
    }, 30_000);

    return () => {
      clearInterval(tick);
      clearInterval(check);
    };
  }, []);

  return seconds;
}

const GearIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9c.14.35.4.64.73.83.29.17.62.26.96.26H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

const RocketIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
    <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
    <path d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" />
    <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
    <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
  </svg>
);

export function MaintenancePage({ mode = 'maintenance' }: { mode?: Mode }) {
  const logo = useBrandLogo();
  const seconds = useAutoRecover();
  const copy = COPY[mode];
  // This screen exists to reassure people, so a broken-image icon is the one
  // thing it must never show. If even the built-in logo fails, fall back to the
  // brand name set as type.
  const [logoBroken, setLogoBroken] = useState(false);
  const name = brand.displayName ?? brand.name;

  return (
    <div className="mnt-root">
      <style>{CSS}</style>

      {/* Ambient brand-tinted glow — purely decorative. */}
      <span className="mnt-orb mnt-orb-a" aria-hidden="true" />
      <span className="mnt-orb mnt-orb-b" aria-hidden="true" />
      <span className="mnt-grid" aria-hidden="true" />

      <main className="mnt-card" role="status" aria-live="polite">
        {logoBroken ? (
          <p className="mnt-wordmark">{name}</p>
        ) : (
          <img
            src={logo}
            alt={name}
            className="mnt-logo"
            onError={() => setLogoBroken(true)}
          />
        )}

        <div className="mnt-icon" aria-hidden="true">
          <span className="mnt-ring" />
          <span className="mnt-ring mnt-ring-2" />
          <span className={mode === 'maintenance' ? 'mnt-glyph mnt-spin' : 'mnt-glyph mnt-float'}>
            {mode === 'maintenance' ? <GearIcon /> : <RocketIcon />}
          </span>
        </div>

        <span className="mnt-badge">
          <span className="mnt-dot" />
          {copy.badge}
        </span>

        <h1 className="mnt-title">{copy.title}</h1>
        <p className="mnt-lead">{copy.lead}</p>
        <p className="mnt-sub">{copy.sub}</p>

        <div className="mnt-bar" aria-hidden="true">
          <span />
        </div>

        <p className="mnt-retry">
          Rechecking in <b>{seconds}s</b> — page apne aap khul jayega.
        </p>

        <div className="mnt-actions">
          <button type="button" className="mnt-btn" onClick={() => window.location.reload()}>
            Try again
          </button>
          {brand.supportEmail && (
            <a className="mnt-btn mnt-btn-ghost" href={`mailto:${brand.supportEmail}`}>
              Contact support
            </a>
          )}
        </div>
      </main>

      <footer className="mnt-foot">
        © {new Date().getFullYear()} {name}
      </footer>
    </div>
  );
}

export default MaintenancePage;

/* Scoped so this screen renders identically no matter which layout/CSS reset
   the brand ships. Colours come from the brand theme variables with hard
   fallbacks, so it is still correct if it paints before the theme lands. */
const CSS = `
.mnt-root{position:fixed;inset:0;z-index:9999;display:flex;flex-direction:column;
  align-items:center;justify-content:center;gap:18px;padding:24px;overflow:hidden;
  background:
    radial-gradient(1200px 600px at 50% -10%, color-mix(in srgb, var(--brand-primary,#0088cc) 45%, transparent) 0%, transparent 60%),
    linear-gradient(160deg,#071a26 0%,#0a2233 45%,#050d14 100%);
  color:#e8f2f8;font-family:'Roboto Condensed',system-ui,-apple-system,sans-serif;}

.mnt-grid{position:absolute;inset:0;opacity:.16;pointer-events:none;
  background-image:linear-gradient(color-mix(in srgb,var(--brand-primary,#0088cc) 55%,transparent) 1px,transparent 1px),
                   linear-gradient(90deg,color-mix(in srgb,var(--brand-primary,#0088cc) 55%,transparent) 1px,transparent 1px);
  background-size:46px 46px;
  -webkit-mask-image:radial-gradient(circle at 50% 40%,#000 0%,transparent 72%);
          mask-image:radial-gradient(circle at 50% 40%,#000 0%,transparent 72%);}

.mnt-orb{position:absolute;border-radius:50%;filter:blur(90px);opacity:.5;pointer-events:none;
  background:var(--brand-primary,#0088cc);animation:mnt-drift 14s ease-in-out infinite;}
.mnt-orb-a{width:420px;height:420px;top:-140px;left:-120px;}
.mnt-orb-b{width:360px;height:360px;bottom:-150px;right:-110px;animation-delay:-7s;opacity:.35;}
@keyframes mnt-drift{0%,100%{transform:translate3d(0,0,0) scale(1)}50%{transform:translate3d(40px,30px,0) scale(1.12)}}

.mnt-card{position:relative;width:100%;max-width:560px;text-align:center;padding:38px 28px 32px;
  border-radius:22px;border:1px solid rgba(255,255,255,.12);
  background:rgba(255,255,255,.055);backdrop-filter:blur(14px);-webkit-backdrop-filter:blur(14px);
  box-shadow:0 24px 70px rgba(0,0,0,.5);animation:mnt-in .5s cubic-bezier(.2,.7,.3,1) both;}
@keyframes mnt-in{from{opacity:0;transform:translateY(18px) scale(.97)}to{opacity:1;transform:none}}

.mnt-logo{height:54px;max-width:210px;object-fit:contain;margin:0 auto 22px;display:block;
  filter:drop-shadow(0 6px 16px rgba(0,0,0,.45));}
.mnt-wordmark{margin:0 0 22px;font-size:24px;font-weight:800;letter-spacing:.08em;
  text-transform:uppercase;color:#fff;text-shadow:0 4px 16px rgba(0,0,0,.5);}

.mnt-icon{position:relative;width:96px;height:96px;margin:0 auto 20px;display:grid;place-items:center;}
.mnt-ring{position:absolute;inset:0;border-radius:50%;border:2px solid var(--brand-primary,#0088cc);
  opacity:.55;animation:mnt-pulse 2.6s ease-out infinite;}
.mnt-ring-2{animation-delay:1.3s;}
@keyframes mnt-pulse{0%{transform:scale(.7);opacity:.6}100%{transform:scale(1.45);opacity:0}}
.mnt-glyph{width:52px;height:52px;color:var(--brand-primary,#0088cc);
  filter:drop-shadow(0 0 14px color-mix(in srgb,var(--brand-primary,#0088cc) 70%,transparent));}
.mnt-glyph svg{width:100%;height:100%;}
.mnt-spin{animation:mnt-rotate 9s linear infinite;}
@keyframes mnt-rotate{to{transform:rotate(360deg)}}
.mnt-float{animation:mnt-bob 3s ease-in-out infinite;}
@keyframes mnt-bob{0%,100%{transform:translateY(-4px)}50%{transform:translateY(4px)}}

.mnt-badge{display:inline-flex;align-items:center;gap:8px;padding:6px 14px;border-radius:999px;
  font-size:11px;font-weight:700;letter-spacing:.14em;text-transform:uppercase;
  color:var(--brand-primary,#0088cc);
  background:color-mix(in srgb,var(--brand-primary,#0088cc) 16%,transparent);
  border:1px solid color-mix(in srgb,var(--brand-primary,#0088cc) 40%,transparent);}
.mnt-dot{width:7px;height:7px;border-radius:50%;background:currentColor;animation:mnt-blink 1.6s ease-in-out infinite;}
@keyframes mnt-blink{0%,100%{opacity:1}50%{opacity:.25}}

.mnt-title{margin:16px 0 6px;font-size:30px;line-height:1.15;font-weight:800;letter-spacing:-.4px;color:#fff;}
.mnt-lead{margin:0 0 10px;font-size:17px;font-weight:700;color:var(--brand-primary,#0088cc);}
.mnt-sub{margin:0 auto;max-width:430px;font-size:14px;line-height:1.65;color:rgba(232,242,248,.72);}

.mnt-bar{margin:26px auto 12px;width:min(280px,80%);height:5px;border-radius:999px;overflow:hidden;
  background:rgba(255,255,255,.1);}
.mnt-bar span{display:block;height:100%;width:38%;border-radius:999px;
  background:linear-gradient(90deg,transparent,var(--brand-primary,#0088cc),transparent);
  animation:mnt-sweep 1.9s ease-in-out infinite;}
@keyframes mnt-sweep{0%{transform:translateX(-110%)}100%{transform:translateX(320%)}}

.mnt-retry{margin:0;font-size:12.5px;color:rgba(232,242,248,.55);}
.mnt-retry b{color:#fff;font-variant-numeric:tabular-nums;}

.mnt-actions{display:flex;flex-wrap:wrap;gap:10px;justify-content:center;margin-top:22px;}
.mnt-btn{appearance:none;cursor:pointer;text-decoration:none;display:inline-flex;align-items:center;
  justify-content:center;min-width:150px;padding:11px 22px;border-radius:10px;border:0;
  font-size:14px;font-weight:700;color:#fff;transition:transform .15s ease,filter .15s ease;
  background:linear-gradient(135deg,var(--brand-header-from,#0a93d4),var(--brand-header-to,#005f9e));
  box-shadow:0 8px 22px color-mix(in srgb,var(--brand-primary,#0088cc) 35%,transparent);}
.mnt-btn:hover{transform:translateY(-1px);filter:brightness(1.08);}
.mnt-btn:active{transform:translateY(0) scale(.98);}
.mnt-btn-ghost{background:transparent;box-shadow:none;
  border:1px solid rgba(255,255,255,.22);color:rgba(232,242,248,.85);}
.mnt-btn-ghost:hover{background:rgba(255,255,255,.07);}

.mnt-foot{position:relative;font-size:11.5px;letter-spacing:.06em;color:rgba(232,242,248,.4);}

@media (max-width:480px){
  .mnt-card{padding:30px 20px 26px;border-radius:18px;}
  .mnt-title{font-size:24px;}
  .mnt-lead{font-size:15px;}
  .mnt-logo{height:44px;margin-bottom:18px;}
  .mnt-wordmark{font-size:19px;margin-bottom:18px;}
  .mnt-btn{flex:1 1 100%;}
}
@media (prefers-reduced-motion:reduce){
  .mnt-orb,.mnt-ring,.mnt-spin,.mnt-float,.mnt-dot,.mnt-bar span,.mnt-card{animation:none!important;}
}
`;
